/** Phase 4.1.1 — Dashboard "Autopilot status" tile.
 *
 * Source of truth shape: GET /api/v1/autopilot/dashboard-summary
 *   { mode, optimization_target, last_run_at, last_run_id,
 *     last_optimization_agent_run_id, runs_executed_7d, actions_pending_approval }
 *
 * The `last_optimization_agent_run_id` chip is rendered as a disabled affordance
 * with a friendly tooltip until the agent-run inspector ships in Phase 5. We
 * explicitly choose honesty over broken nav (the user's "smart enhancement").
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useT } from "../../lib/i18n";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";

function rel(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch (e) {
    return iso;
  }
}

export default function AutopilotStatusTile() {
  const t = useT();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/autopilot/dashboard-summary", { skipPermissionToast: true })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []);

  if (error) return null; // silent — non-critical tile
  if (!data) {
    return (
      <Card data-testid="autopilot-tile-loading">
        <CardHeader><CardTitle className="text-base">{t("autopilot_tile.title")}</CardTitle></CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">{t("common.loading")}</p>
        </CardContent>
      </Card>
    );
  }

  const hasRuns = !!data.last_run_at;

  return (
    <Card data-testid="autopilot-status-tile">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base">{t("autopilot_tile.title")}</CardTitle>
        <Badge variant="outline" className="font-mono text-[10px]"
          data-testid="autopilot-tile-mode">
          {data.mode || "—"}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {!hasRuns ? (
          <p className="text-muted-foreground" data-testid="autopilot-tile-empty">
            {t("autopilot_tile.empty")}{" "}
            <Link to="/app/autopilot" className="underline" data-testid="autopilot-tile-empty-link">
              {t("autopilot_tile.empty_cta")}
            </Link>
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {t("autopilot_tile.runs_7d")}
                </div>
                <div className="text-2xl font-semibold tabular-nums"
                  data-testid="autopilot-tile-runs-7d">
                  {data.runs_executed_7d ?? 0}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  {t("autopilot_tile.pending")}
                </div>
                <div className="text-2xl font-semibold tabular-nums"
                  data-testid="autopilot-tile-pending">
                  {data.actions_pending_approval ?? 0}
                </div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground" data-testid="autopilot-tile-last-run">
              {t("autopilot_tile.last_run")} {rel(data.last_run_at) || "—"}
            </div>
            {data.last_optimization_agent_run_id && (
              <div className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">{t("autopilot_tile.agent_label")}</span>
                <Link
                  to={`/app/agents/runs/${data.last_optimization_agent_run_id}`}
                  data-testid="autopilot-tile-agent-chip"
                  className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[10px] hover:bg-accent hover:text-accent-foreground transition-colors"
                >
                  {data.last_optimization_agent_run_id.slice(0, 8)}…
                </Link>
              </div>
            )}
            <div>
              <Link to={data.last_run_id ? `/app/autopilot/runs/${data.last_run_id}` : "/app/autopilot"}
                className="text-xs underline" data-testid="autopilot-tile-open-link">
                {t("autopilot_tile.open")}
              </Link>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
